<template>
  <div class="benefit-text-container">
    <!-- Handle merged benefit texts -->
    <template v-if="merged && merged.length !== 0">
      <div v-for="(item, index) in groupedBenefits" :key="index" class="benefit-group">
        <div class="text-content">
          <pre v-html="highlightRemaining(item.text)"></pre>
        </div>
        <ul v-if="item.notes && item.notes.length" class="notes-list">
          <li
            v-for="(note, noteIndex) in getUniqueNotes(item.notes)"
            :key="noteIndex"
            class="note-item"
            :class="{ 'note-heading': getHeading(note) && !getHeading(note).value }"
          >
            <template v-if="getHeading(note)">
              <strong>{{ getHeading(note).label }}{{ getHeading(note).value ? ':' : '' }}</strong>
              {{ getHeading(note).value }}
            </template>
            <template v-else-if="getProviderNotes(note)">
              <strong>{{ getProviderNotes(note).label.toLowerCase() }}:</strong>
              <ul class="notes-list">
                <li v-for="(provider, index) in getProviderNotes(note).providers" :key="index" class="note-item">
                  {{ (provider.description ? `${provider.description} (${provider.code})` : provider.code).toLowerCase() }}
                </li>
              </ul>
            </template>
            <template v-else-if="getNoteParts(note).length">
              <strong>{{ getNoteParts(note)[0] }}</strong>
              <div>{{ getNoteParts(note).slice(1).join(' · ') }}</div>
            </template>
            <template v-else>
              <span class="note-text">{{ formatNote(note) }}</span>
            </template>
          </li>
        </ul>
      </div>
    </template>
    <!-- Handle single benefit text -->
    <template v-else-if="text || (notes && notes.length)">
      <div :class="{ 'benefit-group': !accumulationTexts.length }">
        <template v-if="accumulationTexts.length">
          <div v-for="(amountText, index) in accumulationTexts" :key="index" class="benefit-group">
            <div class="text-content">
              <pre v-html="highlightRemaining(amountText)"></pre>
            </div>
          </div>
          <div v-if="notes && notes.length" class="has-text-grey">Payer notes</div>
        </template>
        <div class="text-content" v-else-if="text">
          <pre v-html="highlightRemaining(text)"></pre>
        </div>
        <ul v-if="notes && notes.length" class="notes-list">
          <li
            v-for="(note, index) in getUniqueNotes(notes)"
            :key="index"
            class="note-item"
            :class="{ 'note-heading': getHeading(note) && !getHeading(note).value }"
          >
            <template v-if="getHeading(note)">
              <strong>{{ getHeading(note).label }}{{ getHeading(note).value ? ':' : '' }}</strong>
              {{ getHeading(note).value }}
            </template>
            <template v-else-if="getProviderNotes(note)">
              <strong>{{ getProviderNotes(note).label.toLowerCase() }}:</strong>
              <ul class="notes-list">
                <li v-for="(provider, index) in getProviderNotes(note).providers" :key="index" class="note-item">
                  {{ (provider.description ? `${provider.description} (${provider.code})` : provider.code).toLowerCase() }}
                </li>
              </ul>
            </template>
            <template v-else-if="getNoteParts(note).length">
              <strong>{{ getNoteParts(note)[0] }}</strong>
              <div>{{ getNoteParts(note).slice(1).join(' · ') }}</div>
            </template>
            <template v-else>
              <span class="note-text">{{ formatNote(note) }}</span>
            </template>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BenefitText',
  props: {
    merged: {
      type: Array,
      default: () => [],
    },
    text: {
      type: String,
      default: '',
    },
    notes: {
      type: Array,
      default: () => [],
    },
    accumulation: {
      type: Boolean,
      default: false,
    },
    planNotes: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    providerDefinitions() {
      const definitions = new Map();
      for (const note of this.planNotes || []) {
        const text = note && typeof note === 'object' ? note.message : note;
        if (typeof text !== 'string') continue;
        if (!/^(?:BENEFIT ABBREVIATIONS\s*-\s*)?[A-Z0-9]{2,3}\s*=/i.test(text.trim())) continue;
        const entries = text.trim()
          .replace(/^BENEFIT ABBREVIATIONS\s*-\s*/i, '')
          .split(/,\s*(?=[A-Z0-9]{2,3}\s*=)/i);
        for (const entry of entries) {
          const match = entry.match(/^([A-Z0-9]{2,3})\s*=\s*(.+)$/i);
          if (!match) continue;
          const code = match[1].toUpperCase();
          const description = match[2].replace(/,\s*$/, '').trim();
          if (!description) continue;
          definitions.set(
            code,
            definitions.has(code) && definitions.get(code) !== description ? null : description
          );
        }
      }
      return definitions;
    },
    accumulationTexts() {
      if (!this.accumulation || !this.text) return [];
      const texts = this.text.split(/,\r?\n/);
      return texts.length > 1 && texts.every((text) => /^\$[\d,.]+(?:\s|$)/.test(text))
        ? texts
        : [];
    },
    groupedBenefits() {
      const benefits = [];
      const groups = new Map();
      for (const item of this.merged || []) {
        const { notes, ...information } = item;
        if (!item.text || (notes || []).some((note) => this.getHeading(note))) {
          benefits.push(item);
          continue;
        }
        const key = JSON.stringify(
          Object.keys(information)
            .sort()
            .map((field) => [field, information[field]])
        );
        if (!groups.has(key)) {
          const benefit = { ...item, notes: [] };
          groups.set(key, benefit);
          benefits.push(benefit);
        }
        const benefit = groups.get(key);
        for (const note of notes || []) {
          if (!benefit.notes.some((existing) => JSON.stringify(existing) === JSON.stringify(note))) {
            benefit.notes.push(note);
          }
        }
      }
      return benefits;
    },
  },
  methods: {
    getUniqueNotes(notes) {
      if ((notes || []).some((note) => this.getHeading(note) || this.getNoteParts(note).length)) {
        return notes.filter((note, index, values) => {
          const message = typeof note === 'string' ? note : note?.message;
          const previous = typeof values[index - 1] === 'string' ? values[index - 1] : values[index - 1]?.message;
          return !index || message !== previous || note?.type !== values[index - 1]?.type ||
            this.getHeading(note) || this.getNoteParts(note).length || !/^[^:\r\n]+:\s*\S/.test(message);
        });
      }
      const seen = new Set();
      return (notes || []).filter((note) => {
        const message = note && typeof note === 'object' ? note.message : note;
        if (typeof message !== 'string' || /https?:\/\/|www\.|@/i.test(message)) return true;
        const key = JSON.stringify(
          typeof note === 'object' ? { ...note, message: message.toLowerCase() } : message.toLowerCase()
        );
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    getProviderNotes(note) {
      const text = note && typeof note === 'object' ? note.message : note;
      if (typeof text !== 'string') return null;
      const match = text.trim().match(
        /^(INCLUDED|EXCLUDED) PROVIDER (TYPES|SPECIALTIES)\s*-\s*([A-Z0-9]{2,3}(?:,\s*[A-Z0-9]{2,3})*)$/i
      );
      if (!match) return null;
      const providers = match[3].split(',').map((code) => ({
        code: code.trim(),
        description: this.providerDefinitions.get(code.trim().toUpperCase()),
      }));
      if (!providers.some((provider) => provider.description)) return null;
      return { label: `${match[1].toLowerCase()} provider ${match[2].toLowerCase()}`, providers };
    },
    getHeading(note) {
      const text = note && typeof note === 'object' ? note.message : note;
      if (typeof text !== 'string') return '';
      const match = text.match(/^\s*\[([^[\]\r\n]+)\][ \t]*(?::[ \t]*([\s\S]*))?\s*$/);
      return match && match[1].trim() ? { label: match[1].trim(), value: match[2] || '' } : '';
    },
    getNoteParts(note) {
      const text = note && typeof note === 'object' ? note.message : note;
      if (typeof text !== 'string') return [];
      if (!/^[^_\r\n]+_[^_\r\n]+_(?:in|out of) network_{2,}adj level \d+(?:_[^_\r\n]*)?$/i.test(text)) {
        return [];
      }
      return text.split(/_+/).filter((part) => part.trim());
    },
    formatNote(note) {
      const text = note && typeof note === 'object' ? note.message : note;
      if (typeof text !== 'string') return '';
      let formatted = text.toLowerCase();
      if (!/https?:\/\/|www\./i.test(text)) {
        formatted = formatted.replace(/&(?:#\d+|#x[\da-f]+|[a-z]+);|;[ \t]*(?=\S)/gi, (separator) =>
          separator.startsWith('&') ? separator : ';\n'
        );
      }
      if (/^excluded provider (?:types|specialties)\s*-\s*[a-z0-9]+(?:,\s*[a-z0-9]+)*$/i.test(text)) {
        formatted = formatted.replace(/,(?=[a-z0-9])/gi, ', ');
      }
      return formatted;
    },
    highlightRemaining(text) {
      if (!text) return '';
      return text.replace(/(Remaining|remaining)/g, '<strong>$1</strong>');
    },
  },
};
</script>

<style scoped>
.benefit-text-container {
  margin: 1.5rem 0;
}

.benefit-group {
  border-left: 3px solid #e0e0e0;
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  transition: border-left-color 0.3s ease;
  position: relative;
}

.benefit-group:hover {
  border-left-color: #7957d5;
}

.benefit-group:hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, rgba(121, 87, 213, 0.05), transparent);
  pointer-events: none;
}

.benefit-group:last-child {
  margin-bottom: 0;
}

.text-content {
  margin-bottom: 0.25rem;
  position: relative;
  z-index: 1;
}

.text-content pre {
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 1rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: #363636;
  font-family: inherit;
  line-height: 1.5;
}

.notes-list {
  list-style: none;
  margin: 0 !important;
  padding: 0;
  padding-top: 0.25rem;
}

.note-text {
  white-space: pre-line;
}

.note-item {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
  padding-left: 1.25rem;
  position: relative;
  line-height: 1.4;
}

.note-item:before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: #7957d5;
  font-weight: bold;
}

.note-item:last-child {
  margin-bottom: 0;
}

.note-heading {
  padding-left: 0;
  margin-top: 0.75rem;
}

.note-heading:first-child {
  margin-top: 0;
}

.note-heading:before {
  content: none;
}
</style>
